import {
  type ChangeEvent,
  type FormEvent,
  type FunctionComponent,
  type KeyboardEvent,
  type RefObject,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { TOGGLE_LINK_COMMAND } from "@lexical/link";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";

import { Tooltip } from "../../../general/Tooltip";
import { buildActionSet, deriveDialogMode, deriveUrlStatus, isValidUrl, renderActionButton } from "../configs/urlActions";
import { AddIcon, RemoveIcon } from "./Icons";

import CSS from "../styles.module.css";

export interface RTEDialogDefs {
  readonly initialUrl: string | null;
  readonly onClose: () => void;
  readonly recentUrls: readonly string[];
  readonly onRecordUrl: (url: string) => void;
  readonly onRemoveUrl: (url: string) => void;
  readonly anchorRef: RefObject<HTMLButtonElement | null>;
}

const RECENT_URL_DISPLAY_LIMIT = 5;

export const Popover: FunctionComponent<RTEDialogDefs> = ({ initialUrl, onClose, recentUrls, onRecordUrl, onRemoveUrl, anchorRef }) => {
  const [editor] = useLexicalComposerContext();
  const [mode, setMode] = useState(() => deriveDialogMode(initialUrl));
  const [url, setUrl] = useState(initialUrl ?? "");
  const [showRecent, setShowRecent] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const status = mode === "edit" ? deriveUrlStatus(url) : deriveUrlStatus(initialUrl ?? "");

  const commitLink = () => {
    const link = url.trim();
    if (!link || !isValidUrl(link)) return;
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, { rel: "noopener noreferrer", target: "_blank", url: link });
    onRecordUrl(link);
    onClose();
  };

  const removeLink = () => {
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    onClose();
  };

  const toggleRecents = () => {
    return setShowRecent((prev) => !prev);
  };

  const selectRecent = (recent: string) => {
    setUrl(recent);
    setShowRecent(false);
    inputRef.current?.focus();
  };

  const removeRecent = (recent: string) => {
    onRemoveUrl(recent);
    if (recentUrls.length <= 1) setShowRecent(false);
    inputRef.current?.focus();
  };

  const enterEditMode = () => setMode("edit");

  useEffect(() => {
    if (mode !== "preview") return;
    const dialog = dialogRef.current;
    const firstFocusable = dialog?.querySelector<HTMLElement>("a[href], button:not(:disabled)");
    firstFocusable?.focus();
  }, [mode]);

  const actions = useMemo(
    () =>
      buildActionSet(mode, status, {
        commit: commitLink,
        toggleRecents,
        edit: enterEditMode,
        remove: removeLink,
        close: onClose,
      }),
    [mode, status, initialUrl],
  );

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      const { target } = event;
      if (!(target instanceof Node)) return;
      if (dialogRef.current?.contains(target)) return;
      if (target instanceof Element && target.closest("[role='toolbar']")) return;
      onClose();
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [onClose]);

  useLayoutEffect(() => {
    const dialog = dialogRef.current;
    const anchor = anchorRef.current;
    const parent = dialog?.offsetParent;
    if (!dialog || !anchor || !(parent instanceof HTMLElement)) return;
    const parentRect = parent.getBoundingClientRect();
    const anchorRect = anchor.getBoundingClientRect();
    const start = Math.max(0, anchorRect.left - parentRect.left - parent.clientLeft);
    dialog.style.setProperty("--rte-popover-inset-start", `${start}px`);
  }, [anchorRef, mode]);

  const handleKeyDown = (event: KeyboardEvent<HTMLDialogElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      onClose();
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    commitLink();
  };

  if (mode === "edit") {
    return (
      <dialog ref={dialogRef} open aria-label="Insert link" className={CSS.Popover} onKeyDown={handleKeyDown}>
        <form className={CSS.PopoverForm} onSubmit={handleSubmit}>
          <div className={CSS.PopoverInputRow}>
            <input
              ref={inputRef}
              type="url"
              inputMode="url"
              className={CSS.PopoverInput}
              aria-label="Insert link URL"
              aria-description="Enter a full URL (https://, file://) or a relative/absolute path"
              placeholder="https://"
              value={url}
              autoFocus
              onChange={(event: ChangeEvent<HTMLInputElement>) => setUrl(event.target.value)}
            />
            <div role="group" className={CSS.PopoverActions}>
              {renderActionButton(actions.primary)}
              {renderActionButton(actions.cancel)}
            </div>
          </div>
          {showRecent && status !== "valid" && (
            <ul className={CSS.PopoverRecentList} aria-label="Recent links">
              {recentUrls.length === 0 ? (
                <li className={CSS.PopoverRecentEmpty}>No recent URLs found</li>
              ) : (
                recentUrls.slice(0, RECENT_URL_DISPLAY_LIMIT).map((recent) => (
                  <li key={recent} className={CSS.PopoverRecentRow}>
                    <Tooltip content="Insert URL into field" alignment="top">
                      {(ref) => (
                        <button
                          ref={ref as (el: HTMLButtonElement | null) => void}
                          type="button"
                          className={CSS.PopoverRecentItem}
                          onClick={() => selectRecent(recent)}
                        >
                          <span className={CSS.PopoverRecentText}>{recent}</span>
                          <AddIcon className={CSS.PopoverRecentAdd} />
                        </button>
                      )}
                    </Tooltip>
                    <Tooltip content="Remove from history" alignment="top">
                      {(ref) => (
                        <button
                          ref={ref as (el: HTMLButtonElement | null) => void}
                          type="button"
                          className={CSS.PopoverRecentRemove}
                          aria-label={`Remove ${recent} from history`}
                          onClick={() => removeRecent(recent)}
                        >
                          <RemoveIcon />
                        </button>
                      )}
                    </Tooltip>
                  </li>
                ))
              )}
            </ul>
          )}
        </form>
      </dialog>
    );
  }

  return (
    <dialog ref={dialogRef} open aria-label="Edit link" className={CSS.Popover} onKeyDown={handleKeyDown}>
      <div className={CSS.PopoverPreview}>
        {status === "valid" ? (
          <a className={CSS.PopoverLink} href={initialUrl ?? "#"} target="_blank" rel="noopener noreferrer">
            {initialUrl}
          </a>
        ) : (
          <p className={CSS.PopoverWarning} role="alert">
            This link points to an unsupported or unsafe location.
          </p>
        )}
        <div role="group" className={CSS.PopoverActions}>
          {renderActionButton(actions.primary)}
          {renderActionButton(actions.cancel)}
        </div>
      </div>
    </dialog>
  );
};
